import React, { useEffect, useState, useContext } from 'react';
import DefaultlayoutHOC from '../layouts/Default.layout';
import HeroCarousal from '../components/HeroCarousal/HeroCarousal.component';
import PosterSlider from '../components/PostSlider/PostSlider.component';
import CategoryFilter from '../components/CategoryFilter/CategoryFilter.component';
import FeaturedMovie from '../components/FeaturedMovie/FeaturedMovie.component';
import InfiniteFeed from '../components/InfiniteFeed/InfiniteFeed.component';
import NewOnOtt from '../components/ott/NewOnOtt.component';
import Poster from '../components/poster/Poster.component';
import { MovieContext } from '../components/context/Movies.context';
import tmdbService, { describeTmdbError } from '../services/tmdb';
import { movieFeed } from '../services/tmdbFeed';
import useInfiniteFeed from '../hooks/useInfiniteFeed';
import { FaFilm, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const { search, myList, continueWatching } = useContext(MovieContext);
  const [searchResults, setSearchResults] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [upcomingMovies, setUpcomingMovies] = useState([]);

  const hasApiKey = Boolean(process.env.REACT_APP_API_KEY);

  // Future releases are excluded from every feed page by design, so the Upcoming
  // lane is fetched separately from /movie/upcoming.
  useEffect(() => {
    let isMounted = true;
    tmdbService
      .getUpcoming(1)
      .then((results) => {
        if (isMounted) setUpcomingMovies(results || []);
      })
      .catch((err) => {
        console.error(describeTmdbError('/movie/upcoming', err));
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // One continuous newest-first stream of Indian movies. Scroll down and TMDB
  // page N+1 keeps appending — no fixed lanes, no "Load More" ceiling.
  const feed = useInfiniteFeed({
    kind: 'movie',
    fetcher: movieFeed.fetch,
    sort: 'newest',
    genreId: activeCategory,
  });

  // Search execution
  useEffect(() => {
    let isMounted = true;
    if (search && search.trim()) {
      const execSearch = async () => {
        try {
          const results = await tmdbService.searchMovies(search);
          if (isMounted) setSearchResults(results || []);
        } catch (err) {
          if (isMounted) setSearchResults([]);
        }
      };
      execSearch();
    } else {
      setSearchResults([]);
    }
    return () => {
      isMounted = false;
    };
  }, [search]);

  // Spotlight pulled from the feed itself, so it always reflects what is on screen.
  const featuredMovie =
    feed.items.find((m) => m.backdrop_path && m.vote_average >= 7) || feed.items[0];

  // Search Results View
  if (search && search.trim()) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-black text-white">
              Search Results for <span className="text-accent-gold">"{search}"</span>
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Found {searchResults.length} matching titles from TMDB
            </p>
          </div>

          {searchResults.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {searchResults.map((movie) => (
                <Poster {...movie} key={movie.id} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-dark-800/50 rounded-2xl border border-white/5 p-8">
              <FaFilm className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-300 text-base font-bold mb-1">Nothing to show</p>
              <p className="text-gray-500 text-sm">No TMDB movies found matching "{search}".</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Carousel */}
      <HeroCarousal />

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Category Pills Filter — drives the feed's `with_genres` server-side */}
        <div className="mb-8">
          <CategoryFilter
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        <NewOnOtt />

        {/* Continue Watching — personal, small, stays a slider */}
        {continueWatching && continueWatching.length > 0 && (
          <section className="mb-12">
            <PosterSlider
              title="Continue Watching"
              subtitle="Pick up where you left off — synced across your devices"
              posters={continueWatching.map((x) => ({
                ...x,
                title: x.title || x.name,
                release_date: x.release_date || null,
              }))}
              isDark={true}
            />
          </section>
        )}

        {/* THE MAIN FEED — latest Indian movies, newest first, infinite scroll */}
        <section className="mb-14">
          <header className="mb-6 flex items-end justify-between gap-4 px-1">
            <div>
              <div className="flex items-center gap-3 mb-1.5">
                <span className="w-1.5 h-7 bg-accent-gold rounded-full" aria-hidden="true" />
<h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">
                    Latest Movies
                  </h2>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm pl-[1.125rem]">
                Bollywood, South Indian, Hollywood (Hindi) &amp; Gujarati — newest first. Keep scrolling to load more.
              </p>
            </div>
            {feed.totalResults > 0 && (
              <span className="hidden sm:inline-block text-[11px] font-bold text-accent-gold bg-accent-gold/10 border border-accent-gold/20 px-3 py-1.5 rounded-full whitespace-nowrap">
                {feed.totalResults.toLocaleString()}+ titles
              </span>
            )}
          </header>

          <InfiniteFeed
            items={feed.items}
            status={feed.status}
            error={feed.error}
            hasMore={feed.hasMore}
            totalResults={feed.totalResults}
            isInitialLoading={feed.isInitialLoading}
            isLoadingMore={feed.isLoadingMore}
            onLoadMore={feed.loadMore}
            onRetry={feed.retry}
            label="Latest movies"
            noun="movies"
            skeletonCount={12}
            emptyState={
              <div className="text-center py-20 bg-dark-800/60 rounded-3xl border border-white/10 p-8 max-w-2xl mx-auto shadow-2xl">
                <div className="w-16 h-16 bg-accent-gold/10 text-accent-gold rounded-full flex items-center justify-center mx-auto mb-4 border border-accent-gold/20">
                  <FaFilm className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-black text-white mb-2">Nothing to show right now</h2>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  {hasApiKey
                    ? 'No Indian movies are currently returned by the TMDB API for this selection.'
                    : 'To display live movies directly from TMDB with zero dummy data, configure your TMDB API Key in the .env file.'}
                </p>
                {!hasApiKey && (
                  <div className="inline-flex items-center gap-2 bg-dark-700 text-gray-300 text-xs px-4 py-2.5 rounded-xl border border-white/10 mb-6 font-mono">
                    <FaInfoCircle className="text-accent-gold w-4 h-4" />
                    <span>REACT_APP_API_KEY=your_tmdb_api_key</span>
                  </div>
                )}
                <div>
                  <Link
                    to="/movies"
                    className="inline-block bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-extrabold text-xs px-6 py-3 rounded-xl transition-all shadow-lg"
                  >
                    Browse Movies Catalog
                  </Link>
                </div>
              </div>
            }
          />
        </section>

        {/* Spotlight Featured */}
        {featuredMovie && (
          <section className="mb-14">
            <FeaturedMovie movie={featuredMovie} />
          </section>
        )}

        {/* My Watchlist Section */}
        {myList && myList.length > 0 && (
          <section className="mb-12">
            <PosterSlider
              title="My Watchlist"
              subtitle={`${myList.length} saved for later`}
              posters={myList}
              isDark={true}
            />
          </section>
        )}

        {/* Upcoming Movies — the one lane that is intentionally NOT part of the
            released feed above. Future releases have no place in a newest-first
            catalog, so they stay here as a separate, finite strip. */}
        {upcomingMovies.length > 0 && (
          <section className="mb-12">
            <PosterSlider
              title="Upcoming Movies"
              subtitle="Not yet released — coming soon to theaters"
              posters={upcomingMovies}
              isDark={true}
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default DefaultlayoutHOC(HomePage);
